import { useState } from 'react';
import { Lock, Shield, Key, Globe, Eye, EyeOff, Link, Unlink } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { PollinationsConnect } from '../components/PollinationsConnect';
import { useAppState } from '../hooks/useAppState';

const ADMIN_PASSWORD = 'flashcardgenerator-admin-2026';

export const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { settings, updateSettings } = useAppState();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle>Admin Dashboard</CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Enter password to access admin settings
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
              )}
              <Button type="submit" className="w-full" disabled={!password.trim()}>
                <Lock className="w-4 h-4 mr-2" />
                Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage AI providers and app settings
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          Lock
        </Button>
      </div>

      {/* AI Provider Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-blue-600" />
            <CardTitle>AI Provider Configuration</CardTitle>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            BYOP (Bring Your Own Pollen) - Users connect their own Pollinations accounts
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This app uses Pollinations AI's BYOP system. Users authenticate with their own Pollinations account and use their Pollen credits for AI generation.
          </p>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              How BYOP Works:
            </h4>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>1. User clicks "Connect Pollinations Account"</li>
              <li>2. Redirected to Pollinations OAuth page</li>
              <li>3. User authorizes the app with their Pollinations account</li>
              <li>4. API key stored in user's browser localStorage</li>
              <li>5. API requests use user's Pollen credits</li>
            </ul>
          </div>

          <PollinationsConnect compact={false} />

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Marketplace Submission:
            </h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li>• Register your App Key at <strong>enter.pollinations.ai</strong></li>
              <li>• Set Redirect URI to your app's callback URL</li>
              <li>• Submit your live URL to the Pollinations marketplace</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* App Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-green-600" />
            <CardTitle>App Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Version</span>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Build</span>
              <p className="font-medium">Cloudflare Pages</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Framework</span>
              <p className="font-medium">React + Vite</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Styling</span>
              <p className="font-medium">Tailwind CSS 4</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;